/**
 * Users Page - View and copy list of registered users (admin only)
 */

import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { PageContent } from '../components/PageContent';
import { getUsers, type User } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Copy, Check } from 'lucide-react';

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch {
      // Fallback - no users in demo
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const handleCopyUsers = async () => {
    const csv = users
      .map((u) => `${u.fullName}, ${u.email}, ${u.orgName}, ${u.date}, ${u.ip}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(csv);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Registered Users"
        description="View and export user registrations from the QVT Demo project"
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyUsers}
            disabled={users.length === 0}
            data-testid="copy-users-button"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy CSV
              </>
            )}
          </Button>
        }
      />
      <PageContent width="xl">
        <Card>
          <CardContent className="p-6">
            {loading ? (
              <p className="text-muted-foreground text-center py-8">Loading users...</p>
            ) : users.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No users registered yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.email}
                      data-user-email={user.email}
                      className="transition-colors hover:bg-muted/50"
                    >
                      <TableCell>{user.fullName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.orgName}</TableCell>
                      <TableCell>{user.date}</TableCell>
                      <TableCell>{user.ip}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </PageContent>
    </Layout>
  );
}
